import { captureFrameRateConstraint, normalizeCaptureLimit } from '@/core/performance-settings'
import { captureVideoBitmap } from '@/core/video-bitmap'
import { performanceDiagnostics } from './performance-diagnostics'
import { RankCaptureCheck } from './rank-capture-check'
import { CaptureCompatibilityError, checkCaptureCompatibility, type CaptureCompatibilityIssue } from './capture-compatibility'

export type CaptureStatus = 'idle' | 'requesting' | 'selected' | 'connecting' | 'active' | 'interrupted' | 'error'
export type CaptureMediaState = 'idle' | 'playing' | 'waiting' | 'stalled' | 'paused' | 'muted' | 'unmuted'
export type CaptureFailureKind = CaptureCompatibilityIssue | 'permission-denied' | 'security-restricted' | 'invalid-state' | 'source-unavailable' | 'timeout' | 'unknown'
export type CaptureSelectionPreference = 'window' | 'monitor'
export type CaptureSurface = CaptureSelectionPreference | 'browser'
export type CaptureFailurePhase = 'selection' | 'track' | 'play' | 'dimensions'

export type CaptureConstraintTier = 'extended' | 'basic-constraints' | 'ideal-frame-rate' | 'unconstrained'

export interface CaptureDiagnostic {
  readonly recordedAt: string
  readonly phase: CaptureFailurePhase
  readonly name: string
  readonly message: string
  readonly requestedSurface: CaptureSelectionPreference
  readonly displaySurface?: CaptureSurface
  readonly track?: { readonly readyState: MediaStreamTrackState; readonly muted: boolean }
  readonly browser: string
  readonly constraintTier?: CaptureConstraintTier
}

export class CaptureSelectionReplacedError extends Error {
  constructor() {
    super('Capture selection was replaced')
    this.name = 'AbortError'
  }
}

export function createCaptureDiagnostic(
  error: unknown,
  phase: CaptureFailurePhase,
  requestedSurface: CaptureSelectionPreference,
  track?: MediaStreamTrack,
  constraintTier?: CaptureConstraintTier,
): CaptureDiagnostic {
  let displaySurface: CaptureSurface | undefined
  let browser = 'unknown'
  // Diagnostics must never prevent failed streams from being released.
  try { displaySurface = captureSurface(track?.getSettings().displaySurface) } catch { /* source may already be unavailable */ }
  try { browser = navigator.userAgent } catch { /* browser details are optional */ }
  return {
    recordedAt: new Date().toISOString(),
    phase,
    name: typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : 'Error',
    message: captureErrorMessage(error),
    requestedSurface,
    displaySurface,
    track: track ? { readyState: track.readyState, muted: track.muted } : undefined,
    browser,
    ...(constraintTier ? { constraintTier } : {}),
  }
}

function captureSurface(value: string | undefined): CaptureSurface | undefined {
  return value === 'window' || value === 'monitor' || value === 'browser' ? value : undefined
}

function captureErrorMessage(error: unknown): string {
  return typeof error === 'object' && error !== null && 'message' in error ? String(error.message) : String(error)
}

export interface CaptureInfo {
  readonly width: number
  readonly height: number
  readonly label: string
  readonly displaySurface?: CaptureSurface
}

interface ExtendedDisplayMediaStreamOptions extends DisplayMediaStreamOptions {
  readonly monitorTypeSurfaces?: 'include' | 'exclude'
  readonly selfBrowserSurface?: 'include' | 'exclude'
  readonly surfaceSwitching?: 'include' | 'exclude'
}

const DISPLAY_CAPTURE_HINTS = {
  monitorTypeSurfaces: 'include',
  selfBrowserSurface: 'exclude',
  surfaceSwitching: 'exclude',
} as const satisfies Pick<ExtendedDisplayMediaStreamOptions, 'monitorTypeSurfaces' | 'selfBrowserSurface' | 'surfaceSwitching'>

function isConstraintRetryError(error: unknown): boolean {
  const name = typeof error === 'object' && error !== null && 'name' in error ? String(error.name) : ''
  return name === 'TypeError' || name === 'OverconstrainedError'
}

export function displayCaptureAttempts(
  preference: CaptureSelectionPreference,
  fpsLimit: number,
): ReadonlyArray<{ readonly tier: CaptureConstraintTier; readonly options: DisplayMediaStreamOptions }> {
  const fps = captureFrameRateConstraint(fpsLimit)
  const extended: ExtendedDisplayMediaStreamOptions = {
    video: { displaySurface: preference, frameRate: fps },
    audio: false,
    ...DISPLAY_CAPTURE_HINTS,
  }
  return [
    { tier: 'extended', options: extended },
    {
      tier: 'basic-constraints',
      options: { video: { displaySurface: preference, frameRate: fps }, audio: false },
    },
    {
      tier: 'ideal-frame-rate',
      options: { video: { displaySurface: preference, frameRate: { ideal: normalizeCaptureLimit(fpsLimit) } }, audio: false },
    },
    { tier: 'unconstrained', options: { video: true, audio: false } },
  ]
}

async function requestDisplayMedia(
  preference: CaptureSelectionPreference,
  fpsLimit: number,
): Promise<{ stream: MediaStream; tier: CaptureConstraintTier }> {
  const attempts = displayCaptureAttempts(preference, fpsLimit)
  let lastError: unknown
  for (const [index, attempt] of attempts.entries()) {
    try {
      return { stream: await navigator.mediaDevices.getDisplayMedia(attempt.options), tier: attempt.tier }
    } catch (error) {
      lastError = error
      if (index === attempts.length - 1 || !isConstraintRetryError(error)) throw error
    }
  }
  throw lastError
}

export function classifyCaptureFailure(error: unknown): CaptureFailureKind {
  if (error instanceof CaptureCompatibilityError) return error.kind
  const name = typeof error === 'object' && error !== null && 'name' in error
    ? String(error.name)
    : ''
  const message = captureErrorMessage(error)

  if (name === 'SecurityError') return 'security-restricted'
  if (name === 'InvalidStateError') return 'invalid-state'
  if (name === 'NotSupportedError') return 'unsupported'

  if (name === 'NotAllowedError' || /permission denied|not allowed by the user|permission dismissed/i.test(message)) {
    return 'permission-denied'
  }
  if (/display capture is not supported/i.test(message)) {
    return 'unsupported'
  }
  if (name === 'NotReadableError'
    || name === 'NotFoundError'
    || name === 'AbortError'
    || name === 'OverconstrainedError'
    || /could not start (?:video )?source|starting (?:video|screen).*failed|failed to (?:start|allocate).*video|video source.*(?:unavailable|busy|in use)/i.test(message)) {
    return 'source-unavailable'
  }
  if (/等待捕获画面超时|capture frame timed out|timed out waiting for capture/i.test(message)) {
    return 'timeout'
  }
  return 'unknown'
}

export class CaptureService extends EventTarget {
  readonly rankCheck = new RankCaptureCheck(() => this.dispatchEvent(new Event('rank-assessment')))
  private preview: HTMLVideoElement
  private stream: MediaStream | null = null
  private playbackClone: MediaStream | null = null
  private selectionRevision = 0
  private cancelConnection: (() => void) | null = null
  private requestedSurface: CaptureSelectionPreference = 'window'
  private requestedFpsLimit = 1
  status: CaptureStatus = 'idle'
  mediaState: CaptureMediaState = 'idle'
  lastError: string | null = null
  lastDiagnostic: CaptureDiagnostic | null = null
  lastConstraintTier: CaptureConstraintTier | null = null

  get selectionId(): number {
    return this.selectionRevision
  }

  get video(): HTMLVideoElement {
    return this.preview
  }

  constructor() {
    super()
    performanceDiagnostics.setCaptureReader(() => {
      const track = this.stream?.getVideoTracks()[0]
      const settings = track?.getSettings()
      return {
        status: this.status,
        requestedFpsLimit: this.requestedFpsLimit,
        reportedFps: settings?.frameRate ?? null,
        width: settings?.width ?? null,
        height: settings?.height ?? null,
        videoWidth: this.video?.videoWidth || null,
        videoHeight: this.video?.videoHeight || null,
        surface: captureSurface(settings?.displaySurface) ?? null,
        constraintTier: this.lastConstraintTier,
      }
    })
    this.preview = this.createPreviewElement()
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => { this.stop() })
      window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) this.replacePreviewElement()
      })
    }
  }

  get supported(): boolean {
    return this.compatibilityIssue === null
  }

  get compatibilityIssue(): CaptureCompatibilityIssue | null {
    return checkCaptureCompatibility()
  }

  async selectWindow(preference: CaptureSelectionPreference = 'window', fpsLimit = 1): Promise<CaptureInfo> {
    this.stop()
    const selectionRevision = ++this.selectionRevision
    this.lastError = null
    this.lastDiagnostic = null
    this.lastConstraintTier = null
    this.requestedSurface = preference
    this.requestedFpsLimit = normalizeCaptureLimit(fpsLimit)
    this.setStatus('requesting')
    let requestedStream: MediaStream | null = null
    try {
      const compatibilityIssue = this.compatibilityIssue
      if (compatibilityIssue) throw new CaptureCompatibilityError(compatibilityIssue)
      const requested = await requestDisplayMedia(preference, this.requestedFpsLimit)
      this.lastConstraintTier = requested.tier
      const stream = requested.stream
      requestedStream = stream
      if (selectionRevision !== this.selectionRevision) {
        throw new CaptureSelectionReplacedError()
      }
      const track = stream.getVideoTracks()[0]
      if (!track) throw new Error('未获得视频轨道')
      this.stream = stream
      track.addEventListener('ended', () => {
        if (this.stream !== stream) return
        this.setStatus('interrupted')
        this.dispatchEvent(new Event('ended'))
      }, { once: true })
      track.addEventListener('mute', () => {
        if (this.stream === stream) this.setMediaState('muted')
      })
      track.addEventListener('unmute', () => {
        if (this.stream === stream) this.setMediaState('unmuted')
      })
      try {
        if ('contentHint' in track) track.contentHint = 'motion'
      } catch { /* contentHint is optional and must not fail selection */ }
      this.replacePreviewElement()
      this.attachSelectedStream(stream)
      this.video.autoplay = true
      this.startPlayback()
      const settings = track.getSettings()
      this.setStatus('selected')
      return {
        width: settings.width || 0,
        height: settings.height || 0,
        label: track.label || 'MapleStory',
        displaySurface: captureSurface(settings.displaySurface),
      }
    } catch (error) {
      if (selectionRevision !== this.selectionRevision) {
        this.discardStream(requestedStream)
        throw new CaptureSelectionReplacedError()
      }
      this.lastDiagnostic = createCaptureDiagnostic(
        error, 'selection', preference, requestedStream?.getVideoTracks()[0], this.lastConstraintTier ?? undefined,
      )
      this.discardStream(requestedStream)
      this.setMediaState('idle')
      this.lastError = `${this.lastDiagnostic.name}: ${this.lastDiagnostic.message}`
      this.setStatus('error')
      throw error
    }
  }

  async connectSelectedWindow(): Promise<CaptureInfo> {
    const selectionRevision = this.selectionRevision
    const stream = this.stream
    const track = stream?.getVideoTracks()[0]
    let phase: CaptureFailurePhase = 'track'
    try {
      if (!stream || !track || track.readyState === 'ended') {
        throw new DOMException('Selected video source is no longer available', 'NotReadableError')
      }
      if (this.status === 'active') return this.captureInfo(track)
      this.lastError = null
      this.lastDiagnostic = null
      this.setStatus('connecting')
      this.video.autoplay = true
      this.attachSelectedStream(stream)
      phase = 'play'
      await this.waitForConnection(track, () => { phase = 'dimensions' })
      if (selectionRevision !== this.selectionRevision) throw new CaptureSelectionReplacedError()
      if (this.stream !== stream || String(track.readyState) === 'ended') {
        throw new DOMException('Selected video source ended while connecting', 'NotReadableError')
      }
      this.rankCheck.connect(selectionRevision, captureSurface(track.getSettings().displaySurface))
      this.setStatus('active')
      this.setMediaState(track.muted ? 'muted' : 'playing')
      return this.captureInfo(track)
    } catch (error) {
      if (selectionRevision !== this.selectionRevision) {
        this.discardStream(stream)
        throw new CaptureSelectionReplacedError()
      }
      this.lastDiagnostic = createCaptureDiagnostic(error, phase, this.requestedSurface, track)
      this.discardStream(stream)
      this.setMediaState('idle')
      this.lastError = `${this.lastDiagnostic.name}: ${this.lastDiagnostic.message}`
      this.setStatus('error')
      throw error
    }
  }

  get appliedFpsLimit(): number { return this.requestedFpsLimit }

  get frameIntervalMs(): number {
    const reported = this.stream?.getVideoTracks()[0]?.getSettings().frameRate
    const fps = reported && reported > 0 ? Math.min(reported, this.requestedFpsLimit) : this.requestedFpsLimit
    return 1_000 / fps
  }

  get mediaStream(): MediaStream | null {
    return this.stream
  }

  async snapshot(): Promise<ImageBitmap> {
    if (this.status !== 'active' || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) throw new Error('捕获画面尚未就绪')
    return captureVideoBitmap(this.video)
  }

  stop(): void {
    this.selectionRevision += 1
    this.rankCheck.reset()
    this.cancelConnection?.()
    const stream = this.stream
    this.stream = null
    for (const track of stream?.getTracks() ?? []) track.stop()
    this.releasePlaybackClone()
    this.replacePreviewElement()
    this.setMediaState('idle')
    if (this.status !== 'idle') this.setStatus('idle')
  }

  private setStatus(status: CaptureStatus): void {
    this.status = status
    if (status === 'error') performanceDiagnostics.error('capture-error')
    this.dispatchEvent(new CustomEvent('status', { detail: status }))
  }

  private setMediaState(state: CaptureMediaState): void {
    if (this.mediaState === state) return
    this.mediaState = state
    this.dispatchEvent(new CustomEvent('media-state', { detail: state }))
  }

  private discardStream(stream: MediaStream | null): void {
    if (!stream) return
    if (this.stream === stream) this.stream = null
    if (this.video.srcObject === stream) this.video.srcObject = null
    for (const track of stream.getTracks()) track.stop()
    this.releasePlaybackClone()
  }

  private captureInfo(track: MediaStreamTrack): CaptureInfo {
    const settings = track.getSettings()
    return {
      width: this.video.videoWidth || settings.width || 0,
      height: this.video.videoHeight || settings.height || 0,
      label: track.label || 'MapleStory',
      displaySurface: captureSurface(settings.displaySurface),
    }
  }

  private attachSelectedStream(stream: MediaStream): void {
    this.mountPreviewElement()
    if (this.video.srcObject !== stream) this.video.srcObject = stream
  }

  private startPlayback(): void {
    try {
      const playback = this.video.play()
      if (typeof playback?.catch === 'function') void playback.catch(() => {})
    } catch { /* connectSelectedWindow still waits for a successful play() */ }
  }

  private createPreviewElement(): HTMLVideoElement {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.autoplay = false
    video.setAttribute('aria-hidden', 'true')
    video.disablePictureInPicture = true
    video.addEventListener('playing', () => {
      if (this.stream) this.setMediaState('playing')
    })
    video.addEventListener('waiting', () => {
      if (this.stream) this.setMediaState('waiting')
    })
    video.addEventListener('stalled', () => {
      if (this.stream) this.setMediaState('stalled')
    })
    video.addEventListener('pause', () => {
      if (this.stream) this.setMediaState('paused')
    })
    return video
  }

  private discardPreviewElement(video: HTMLVideoElement): void {
    try { video.pause() } catch { /* pause() is best-effort while resetting */ }
    video.autoplay = false
    video.srcObject = null
    video.removeAttribute('src')
    if (video.isConnected) video.remove()
  }

  private replacePreviewElement(): void {
    const previous = this.preview
    this.preview = this.createPreviewElement()
    this.discardPreviewElement(previous)
    this.dispatchEvent(new Event('preview'))
  }

  private releasePlaybackClone(): void {
    const clone = this.playbackClone
    this.playbackClone = null
    if (!clone || clone === this.stream) return
    for (const track of clone.getTracks()) track.stop()
  }

  private clonePlaybackStream(stream: MediaStream): MediaStream {
    this.releasePlaybackClone()
    try {
      if (typeof stream.clone === 'function') {
        const cloned = stream.clone()
        if (cloned.getVideoTracks().length > 0) {
          this.playbackClone = cloned
          return cloned
        }
      }
    } catch { /* clone is optional and must not fail connection */ }
    return stream
  }

  private revivePreview(stream: MediaStream, track: MediaStreamTrack): void {
    this.replacePreviewElement()
    this.video.autoplay = true
    this.attachSelectedStream(this.clonePlaybackStream(stream))
    this.startPlayback()
    try {
      const enabled = track.enabled
      track.enabled = false
      track.enabled = enabled
    } catch { /* toggling enabled is a compositor poke only */ }
  }

  private mountPreviewElement(): void {
    if (this.video.isConnected || typeof document === 'undefined' || !document.body) return
    this.video.style.cssText = 'position:fixed;right:8px;bottom:8px;width:80px;height:45px;opacity:0.12;pointer-events:none;z-index:0;border:0'
    document.body.appendChild(this.video)
  }

  private pokeCaptureTrack(track: MediaStreamTrack): void {
    const ImageCaptureCtor = (globalThis as { ImageCapture?: new (track: MediaStreamTrack) => { grabFrame: () => Promise<ImageBitmap> } }).ImageCapture
    if (typeof ImageCaptureCtor !== 'function') return
    try {
      void new ImageCaptureCtor(track).grabFrame().then(bitmap => bitmap.close?.(), () => { /* poke only */ })
    } catch { /* ImageCapture is optional */ }
  }

  private waitForConnection(track: MediaStreamTrack, onPlaying: () => void): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let settled = false
      let playing = false
      let frameCallback = 0
      const stream = this.stream
      const finish = (error?: unknown) => {
        if (settled) return
        settled = true
        cleanup()
        if (error) reject(error)
        else resolve()
      }
      const hasFrame = () => this.video.readyState >= HTMLMediaElement.HAVE_METADATA
        && this.video.videoWidth > 0
        && this.video.videoHeight > 0
      const checkDimensions = () => {
        if (playing && this.video.videoWidth > 0 && this.video.videoHeight > 0) finish()
      }
      const markPlaying = () => {
        if (settled) return
        playing = true
        onPlaying()
        checkDimensions()
      }
      const inspect = () => {
        if (hasFrame()) markPlaying()
      }
      const watchFrames = () => {
        this.video.cancelVideoFrameCallback?.(frameCallback)
        frameCallback = this.video.requestVideoFrameCallback?.(markPlaying) ?? 0
      }
      const listenForPlayback = () => {
        try {
          const playback = this.video.play()
          if (typeof playback?.then === 'function') {
            void playback.then(markPlaying, (error) => {
              if (settled || playing) return
              inspect()
              if (settled || playing) return
              const name = error && typeof error === 'object' && 'name' in error ? String(error.name) : ''
              if (name === 'AbortError') return
              finish(error)
            })
          }
        } catch (error) {
          finish(error)
        }
      }
      const bindInspectListeners = () => {
        this.video.addEventListener('playing', markPlaying)
        this.video.addEventListener('timeupdate', inspect)
        this.video.addEventListener('loadedmetadata', inspect)
        this.video.addEventListener('loadeddata', inspect)
        this.video.addEventListener('resize', inspect)
      }
      const unbindInspectListeners = () => {
        this.video.removeEventListener('playing', markPlaying)
        this.video.removeEventListener('timeupdate', inspect)
        this.video.removeEventListener('loadedmetadata', inspect)
        this.video.removeEventListener('loadeddata', inspect)
        this.video.removeEventListener('resize', inspect)
      }
      const ended = () => finish(new DOMException('Selected video source ended while connecting', 'NotReadableError'))
      const cancel = () => finish(new CaptureSelectionReplacedError())
      const timer = window.setTimeout(() => finish(new Error('Capture frame timed out')), 8_000)
      const poll = window.setInterval(inspect, 50)
      const reviveAt = (delayMs: number) => window.setTimeout(() => {
        if (settled || playing || !stream) return
        unbindInspectListeners()
        this.revivePreview(stream, track)
        bindInspectListeners()
        watchFrames()
        listenForPlayback()
        this.pokeCaptureTrack(track)
      }, delayMs)
      const reviveSoon = reviveAt(600)
      const reviveAgain = reviveAt(2_000)
      const cleanup = () => {
        window.clearTimeout(timer)
        window.clearTimeout(reviveSoon)
        window.clearTimeout(reviveAgain)
        window.clearInterval(poll)
        unbindInspectListeners()
        this.video.cancelVideoFrameCallback?.(frameCallback)
        track.removeEventListener('ended', ended)
        if (this.cancelConnection === cancel) this.cancelConnection = null
      }
      this.cancelConnection = cancel
      bindInspectListeners()
      track.addEventListener('ended', ended, { once: true })
      watchFrames()
      inspect()
      this.pokeCaptureTrack(track)
      listenForPlayback()
    })
  }
}

export const captureService = new CaptureService()
